import '@app/App.css';
import { Button } from '@comps/ui/button';
import CompozzFileUploader from '@comps/ui/CompozzFileUploader';
import CompozzSpinner from '@comps/ui/CompozzSpinner';
import { DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@comps/ui/dialog';
import { CompozzError } from '@dto/CompozzError';
import { ReferenceUsage, UnresolvedReference, Workspace, WorkspaceFileForImport } from '@dto/workspaces';
import { importWorkspaces } from '@utils/model';
import { toastError, toastSuccess } from '@utils/toasts';
import { getWsButtonStyle, TW_CompozzSpinnerContainer, useHover } from '@utils/twClasses';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { translate } from 'src/i18n/i18n';
import { useModelStore } from 'src/store';

type WorkspaceImportWorkspaceDialogContentProps = {
  // Several workspaces are created at once when workspaces which depend on each other are imported together
  onWorkspacesCreated: (workspaces: Workspace[]) => void;
}

function WorkspaceImportWorkspaceDialogContent({ onWorkspacesCreated }: WorkspaceImportWorkspaceDialogContentProps) {

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  // The selected files. They are kept when an import is refused, so that the file of a missing
  // workspace can be added to the selection.
  const [files, setFiles] = useState<File[]>([]);
  // References to shared workspaces which made the last import be refused (nothing was created)
  const [unresolvedReferences, setUnresolvedReferences] = useState<UnresolvedReference[] | null>(null);
  const wsColor = useModelStore((state) => state.wsColor);
  const wsDarkColor = useModelStore((state) => state.wsDarkColor);
  const wsLightColor = useModelStore((state) => state.wsLightColor);
  const retryHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

  function handleWorkspaceImportError(error: string) {
    console.error(error);
  }

  // The component stays mounted when its dialog is closed: forget the last import
  function reset() {
    setFiles([]);
    setUnresolvedReferences(null);
  }

  function handleWorkspacesImport(filesForImport: WorkspaceFileForImport[]) {
    setLoading(true);
    importWorkspaces(
      filesForImport,
      (workspaces: Workspace[]) => {
        reset();
        onWorkspacesCreated(workspaces);
        if (workspaces.length > 1) {
          toastSuccess(translate(t, "workspace.import.success.1", [workspaces.length]));
        } else {
          toastSuccess(t("compozz.file.import.success"));
        }
      },
      (references: UnresolvedReference[]) => {
        setUnresolvedReferences(references);
      },
      (cError: CompozzError | null) => {
        if (cError && cError.i18n) {
          toastError(translate(t, cError.i18n, cError.params));
        } else if (cError && cError.message) {
          toastError(cError.message);
        } else {
          toastError(t("compozz.file.import.error"));
        }
      },
      () => {
        setLoading(false);
      }
    );
  }

  // Describes an unresolved reference: what it is, where it comes from and why it is not resolved
  function describeReference(reference: UnresolvedReference): string {
    // A reference of kind "workspace" stands for a whole shared workspace which is missing
    const isWorkspace = reference.kind === "workspace";
    const kind = t(`workspace.import.unresolved.kind.${reference.kind}`);
    const name = reference.name ? `« ${reference.name} »` : `(${reference.key})`;
    const origin = (!isWorkspace && reference.wsName) ? ` — ${translate(t, "workspace.import.unresolved.workspace.1", [reference.wsName])}` : "";
    let reason: string;
    if (reference.reason === "AMBIGUOUS") {
      reason = translate(t, "workspace.import.unresolved.reason.AMBIGUOUS.1", [(reference.candidates || []).join(", ")]);
    } else if (isWorkspace) {
      reason = t("workspace.import.unresolved.reason.NOT_FOUND_WORKSPACE");
    } else if (reference.wsName) {
      // The shared workspace was found, but not the element inside it
      reason = t("workspace.import.unresolved.reason.NOT_FOUND_IN_WORKSPACE");
    } else {
      reason = t("workspace.import.unresolved.reason.NOT_FOUND");
    }
    return `${kind} ${name}${origin} : ${reason}`;
  }

  // Names an element which uses a reference, with its workspace when several workspaces are imported together
  function describeUsage(usage: ReferenceUsage): string {
    const element = `${t(`workspace.import.unresolved.usage.${usage.kind}`)} « ${usage.name} »`;
    return usage.workspace ? translate(t, "workspace.import.unresolved.usage.workspace.2", [element, usage.workspace]) : element;
  }

  function describeUsages(reference: UnresolvedReference): string {
    return (reference.usages || []).map(describeUsage).join(", ");
  }

  if (loading) {
    return (
      <div className={TW_CompozzSpinnerContainer}>
        <CompozzSpinner />
      </div>
    )
  }
  const scrollStyle = {
    scrollbarColor: wsColor + " " + wsLightColor,
}

  if (unresolvedReferences) {
    return (
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t("workspace.import.unresolved.title")}</DialogTitle>
          <DialogDescription>{translate(t, "workspace.import.unresolved.description.1", [unresolvedReferences.length])}</DialogDescription>
        </DialogHeader>
        <ul className="flex flex-col gap-2 max-h-72 overflow-y-auto text-sm list-disc pl-5 scrollbar-thin scrollbar-button-hidden" style={scrollStyle}>
          {unresolvedReferences.map((reference) => (
            <li key={reference.kind + "-" + reference.key}>
              <span>{describeReference(reference)}</span>
              {reference.usages && reference.usages.length > 0 &&
                <span className="block text-xs text-gray-500">{translate(t, "workspace.import.unresolved.usedby.1", [describeUsages(reference)])}</span>
              }
            </li>
          ))}
        </ul>
        <p className="text-sm">{t("workspace.import.unresolved.help")}</p>
        <div className="flex flex-row justify-end gap-2">
          {/* DialogClose closes the dialog whatever the parent does */}
          <DialogClose asChild>
            <Button variant="outline" className="h-8 min-h-8" onClick={reset}>
              {t("general.button.cancel.text")}
            </Button>
          </DialogClose>
          <Button className="h-8 min-h-8 w-fit px-2" {...retryHover} onClick={() => setUnresolvedReferences(null)}>
            {t("workspace.import.unresolved.retry")}
          </Button>
        </div>
      </DialogContent>
    );
  }

  return (
    <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
      <DialogHeader>
        <DialogTitle>{t("workspace.import.title")}</DialogTitle>
        <DialogDescription hidden={true}>{t("workspace.import.description")}</DialogDescription>
      </DialogHeader>
      <CompozzFileUploader files={files} onFilesChange={setFiles} onImport={handleWorkspacesImport} loading={false} onError={handleWorkspaceImportError} />
    </DialogContent>
  );
};

export default WorkspaceImportWorkspaceDialogContent;
